"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Light-mode hero photo variants; index 0 is the SSR / first-paint default.
 * JPEG versions of the original PNG renders (~250 KB vs ~2 MB each); the
 * backgrounds are photographic, so JPEG compression loss is not visible.
 */
const HERO_VARIANTS = [
  "/cloudaxe_hero_light3-0.jpg",
  "/cloudaxe_hero_light3-1.jpg",
  "/cloudaxe_hero_light3-2.jpg",
  "/cloudaxe_hero_light3-3.jpg",
] as const;

const HERO_DEFAULT = HERO_VARIANTS[0];

function pickRandomVariant(): string {
  const index = Math.floor(Math.random() * HERO_VARIANTS.length);
  return HERO_VARIANTS[index] ?? HERO_DEFAULT;
}

/**
 * Shared homepage atmosphere: light photo hero + white fade, dark mesh/aurora.
 * Pair with root class `home-hero-light` on the page shell.
 *
 * Light hero defaults to `-0.png` on first paint, then picks a fresh random
 * variant on every route load (no session persistence).
 */
export function SiteAtmosphere() {
  const pathname = usePathname();
  const [heroUrl, setHeroUrl] = useState<string>(HERO_DEFAULT);
  const [intakeFocus, setIntakeFocus] = useState(false);

  useEffect(() => {
    setHeroUrl(pickRandomVariant());
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIntakeFocus(root.classList.contains("intake-focus"));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
      <div
        className={`absolute inset-x-0 top-0 h-[min(100dvh,960px)] origin-top bg-[#eef5fb] bg-cover bg-[center_top] bg-no-repeat transition-[filter,transform] duration-500 ease-out will-change-[filter,transform] dark:hidden ${
          intakeFocus ? "scale-105 blur-md brightness-95" : "scale-100 blur-none"
        }`}
        style={{ backgroundImage: `url(${heroUrl})` }}
      />
      <div
        className={`absolute inset-x-0 top-0 h-[min(100dvh,960px)] bg-gradient-to-b transition-colors duration-500 dark:hidden ${
          intakeFocus
            ? "from-white/80 via-white/55 via-40% to-sky-50"
            : "from-white/55 via-white/15 via-45% to-sky-50"
        }`}
      />
      <div className="absolute inset-x-0 top-[min(100dvh,960px)] bottom-0 bg-sky-50 dark:hidden" />
      <div
        className={`absolute inset-0 hidden bg-no-repeat transition-[filter] duration-500 ease-out dark:block animate-mesh-drift ${
          intakeFocus ? "blur-md brightness-75" : "blur-none"
        }`}
        style={{
          backgroundImage:
            "linear-gradient(to top right, rgb(30 58 138 / 0.5) 0%, rgb(30 64 175 / 0.4) 40%, rgb(15 23 42 / 0.95) 70%, rgb(30 58 138 / 0.6) 100%)",
        }}
      />
      <div className="absolute inset-0 hidden opacity-[0.12] dark:block [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:56px_56px]" />
      <div className="absolute -top-32 -left-32 hidden h-[480px] w-[480px] rounded-full bg-blue-500/15 blur-3xl animate-aurora dark:block" />
      <div className="absolute top-1/4 -right-32 hidden h-[520px] w-[520px] rounded-full bg-blue-600/10 blur-3xl animate-aurora-alt dark:block" />
      <div className="absolute bottom-0 left-1/3 hidden h-[420px] w-[420px] rounded-full bg-fuchsia-500/10 blur-3xl animate-aurora dark:block" />
    </div>
  );
}
